import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Tooltip } from '@material-ui/core';
import ModuleContext from 'CMFW/context/ModuleContext';
import Checkbox from 'CMFW/input/Checkbox';
import TextInput from 'CMFW/input/TextInput';
import { ContainerLeft, EditorGap, EditorRow } from 'components/common/styles';
import ActiveInflectTemplateSelect from 'components/selects/ActiveInflectTemplateSelect';
import VocRefLevelSelect, { hasRefLevelScale } from 'components/selects/VocRefLevelSelect';
import { WordClassSelect } from 'components/selects/WordClassSelect';
import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import ListIds from '../../../components/common/ListIds';
import InflectConfig from '../../../components/inflect/InflectConfig';
import { IRefIds } from '../../../constants/refs';
import { IPanelEdit } from '../../../models/PanelsModel';
import { IAppState } from '../../../store/reducers/rootReducer';
import PanelKlines from '../components/PanelKlines';
import PanelLevelIndicator from '../components/PanelLevelIndicator';
import PanelStopInflectPanel from '../components/PanelStopInflectPanel';

const Edit: React.FC = () => {
  const [wordClassKey, setWordClassKey] = useState(0);

  const module = useContext(ModuleContext);
  const item: IPanelEdit = module.getItemUpdatedValue('');
  const courseConfig = useSelector((state: IAppState) => state.courseConfig[module.courseId]);
  const targetDialectId = courseConfig?.targetDialectDefaultId ?? 0;

  // Backend flags decide visibility; on a new panel (no course in the route)
  // fall back to the front whitelist for the course target dialect.
  const isNew = item?.id === -1;
  const transcriptionHidden = isNew ? !hasRefLevelScale(targetDialectId) : item?.transcriptionHidden ?? true;
  const refLevelHidden = isNew ? !hasRefLevelScale(targetDialectId) : item?.refLevelHidden ?? true;

  const handleWordClassChange = () => {
    // Force re-render of ActiveInflectTemplateSelect by changing its key
    setWordClassKey(prev => prev + 1);
  };
  return (
    <ContainerLeft>
      <PanelKlines />
      <EditorGap />
      <TextInput keyName={'concept'} multiline label={'Concept'} />
      <EditorGap />
      <ListIds keyName={'sentences'} refId={IRefIds.sentences} label={'Sentences'} />
      <EditorGap />
      <ListIds keyName={'segments'} refId={IRefIds.segment} label={'Segments'} />
      <EditorGap />
      <ListIds keyName={'compounds'} refId={IRefIds.compounds} label={'Compounds'} />
      <EditorGap />
      <EditorGap />
      <EditorRow>
        <PanelLevelIndicator keyName={'level'} />
        <NoShrink>
          <WordClassSelect isLinked onChange={handleWordClassChange} />
        </NoShrink>
        <TextInputStyled keyName={'item'} label={'Item'} />
        {!transcriptionHidden && <TranscriptionInput keyName={'transcription'} label={'Transcription'} />}
        {!refLevelHidden && <VocRefLevelSelect dialectId={targetDialectId} />}
      </EditorRow>
      <EditorRow>
        <ActiveInflectTemplateSelect key={wordClassKey} isLinked />
      </EditorRow>
      <CheckboxColumn>
        <CheckboxLine>
          <Checkbox keyName={'compoundKeepIrregular'} label={'Compounds: keep irregular'} />
          <InfoTooltip
            title={
              'After inflection, compounds containing this panel keep only the checked cell types. ' +
              'This box keeps the IRREGULAR forms (e.g. estoy). When any box is checked, regular ' +
              'cells are always removed and this panel’s own conjugation covers them. The ' +
              'panel’s own inflect table is never affected. Existing compounds are re-pruned ' +
              'when re-inflected.'
            }
            arrow
          >
            <InfoButton size="small">
              <FontAwesomeIcon icon={faInfoCircle} size="sm" />
            </InfoButton>
          </InfoTooltip>
        </CheckboxLine>
        <CheckboxLine>
          <Checkbox keyName={'compoundKeepChange'} label={'Compounds: keep change'} />
          <InfoTooltip
            title={
              'Keeps the CHANGE forms (stem change: vowel drop, voicing…) in compounds ' +
              'containing this panel. Check both boxes to keep irregular + change (the classic ' +
              'irregular-verb compound). With no box checked, compounds keep the full paradigm.'
            }
            arrow
          >
            <InfoButton size="small">
              <FontAwesomeIcon icon={faInfoCircle} size="sm" />
            </InfoButton>
          </InfoTooltip>
        </CheckboxLine>
      </CheckboxColumn>
      <EditorGap />
      <InflectConfig keyName={'inflects'} />
      <EditorGap />
      <PanelStopInflectPanel keyName={'inflectStopPanel'} />
    </ContainerLeft>
  );
};

const TextInputStyled = styled(TextInput)`
  && {
    margin: 2px 0;
  }
`;

const TranscriptionInput = styled(TextInputStyled)`
  && {
    margin-left: 12px;
  }
`;

const NoShrink = styled.div`
  flex-shrink: 0;
`;

const CheckboxColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const CheckboxLine = styled.div`
  display: flex;
  align-items: center;
`;

const InfoButton = styled(IconButton)`
  && {
    color: #888;
    padding: 4px;
  }
`;

// Wrap Tooltip so styled-components can target the popper class (TextFieldWithInfo pattern)
const InfoTooltip = styled(({ className, ...props }: any) => (
  <Tooltip {...props} classes={{ popper: className }} />
))`
  .MuiTooltip-tooltip {
    font-size: 12px;
    max-width: 320px;
    line-height: 1.4;
  }
`;

export default Edit;
